import { getFeaturedImageAtFirst } from "../RoomTypeSection/utils";

// Room-scoped media for the Room Type Gallery. Reads only the room node, never
// getItemsByCategories, which pools every room's media together.

export type RoomMediaTabId = "photos" | "video_tour" | "3d_tour" | "floor_plan";

export interface RoomMediaItem {
  [key: string]: any;
  /** Carousel media switch: "image" | "videos" | "3D". Matches GalleryCarousel <Media/>. */
  category: string;
  configName?: string;
}

export interface RoomMediaModel {
  photos: RoomMediaItem[];
  videos: RoomMediaItem[];
  threeD: RoomMediaItem[];
  /** Own section, as in the property gallery; counted so the total matches the card badge. */
  floorPlans: RoomMediaItem[];
  totalCount: number;
}

// LayoutV2 reads `base_path` with no fallback; room images carry `path`.
const withBasePath = (img: any): any =>
  img && !img.base_path && img.path ? { ...img, base_path: img.path } : img;

/** `show3D` mirrors the card's own gate; false drops 3D entirely. */
export const getRoomMediaModel = (
  data: any,
  options: { show3D?: boolean } = {},
): RoomMediaModel => {
  const { show3D = true } = options;
  const configName = data?.name;

  const rawImages = Array.isArray(data?.images) ? data.images : [];
  const isFloorPlan = (img: any) => img?.type === "floor_plan";

  const floorPlans: RoomMediaItem[] = rawImages.filter(isFloorPlan).map((image: any) => ({
    ...withBasePath(image),
    category: "floor_plan",
    configName,
  }));

  const photos: RoomMediaItem[] = getFeaturedImageAtFirst(
    rawImages.filter((img: any) => !isFloorPlan(img)),
  ).map((image: any) => ({
    ...withBasePath(image),
    category: "image",
    configName,
  }));

  // Unfiltered, so the gallery shows every video the card strip shows (including
  // `type: null` / "vo_tour").
  const videos: RoomMediaItem[] = (Array.isArray(data?.videos) ? data.videos : []).map(
    (video: any) => ({ ...video, category: "videos", configName }),
  );

  const threeD: RoomMediaItem[] = show3D
    ? (Array.isArray(data?.virtual_views) ? data.virtual_views : []).map((view: any) => ({
        ...view,
        category: "3D",
        caption: "3D",
        configName,
      }))
    : [];

  return {
    photos,
    videos,
    threeD,
    floorPlans,
    totalCount: photos.length + videos.length + threeD.length + floorPlans.length,
  };
};
